import {
  CircularProgress,
  Autocomplete,
  TextField,
  List,
  ListSubheader,
  ListItem,
} from "@mui/material";
import { useGetSavedSearches } from "../../../../hooks/useSavedSearch";
import { SavedSearch } from "../../../../types/saved-search/SavedSearch";
import DeleteSavedSearchDialog from "./DeleteSavedSearchDialog";
import { useContext } from "react";
import { AuthContext } from "../../../../context/AuthContext";
import { useTranslation } from "react-i18next";

interface SavedSearchSelectProps {
  customerId: string;
  selectedSavedSearch: SavedSearch | null;
  onSelectSavedSearchId: (id: string | null) => void;
  handleSavedSearchChange: (value: SavedSearch | null) => void;
  clearSearch: () => void;
}

export default function SavedSearchSelect({
  customerId,
  selectedSavedSearch,
  onSelectSavedSearchId,
  handleSavedSearchChange,
  clearSearch,
}: Readonly<SavedSearchSelectProps>) {
  const { t } = useTranslation("search");
  const auth = useContext(AuthContext);
  const userId = auth.user?.userId ?? "";

  const { data: savedSearches, isLoading } = useGetSavedSearches(
    customerId,
    userId
  );

  return (
    <List
      subheader={
        <ListSubheader
          component="div"
          id="nested-list-subheader"
          sx={{ position: "relative", top: 0 }}
        >
          {t("search:applySearchTemplate")}
        </ListSubheader>
      }
    >
      <ListItem>
        <Autocomplete
          options={savedSearches ?? []}
          value={selectedSavedSearch ?? null}
          fullWidth
          onChange={(_, value) => {
            value?.savedSearchId
              ? onSelectSavedSearchId(value.savedSearchId)
              : clearSearch();
          }}
          getOptionLabel={(option) => option?.name ?? ""}
          isOptionEqualToValue={(option, value) =>
            option.savedSearchId === value?.savedSearchId
          }
          loading={isLoading}
          clearOnEscape
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("search:selectSearch")}
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </ListItem>
      <DeleteSavedSearchDialog
        selectedSavedSearch={selectedSavedSearch}
        handleSavedSearchChange={handleSavedSearchChange}
      />
    </List>
  );
}
